import { Checkbox, Tooltip } from 'antd';
import { useMemo } from 'react';

import { CUSTOM_LENS_TOOLTIP_PROPS } from 'components/molecules/PPA/Modals/CustomLense/customLens.ui';
import {
    PPA_SIM_LENS_CONFIG_APPLY_BTN,
    PPA_SIM_LENS_CONFIG_BODY,
    PPA_SIM_LENS_CONFIG_CONTENT,
    PPA_SIM_LENS_CONFIG_EDIT_BTN,
    PPA_SIM_LENS_CONFIG_FOOTER,
    PPA_SIM_LENS_CONFIG_RESET_BTN,
    PPA_SIM_LENS_CONFIG_SYSTEM_ROW,
    ppaSimLensConfigColumnTestId,
    ppaSimLensConfigOptionTestId,
    ppaSimLensConfigSelectAllTestId,
} from 'constants/ppaTestIds';
import {
    DEFAULT_LENS_DIMENSION_SELECTION_COUNT,
    filterCatalogItems,
    getCatalogSectionItems,
    getCatalogSelectionSummary,
    getDimensionSelectionLimitState,
    getDimensionsSection,
    getKpisSection,
    getSystemColumnItems,
    LENS_CATALOG_SECTION_KEYS,
    LENS_DIMENSION_SELECTION_LIMIT_TOOLTIP,
    isLensCatalogApplyEnabled,
    isLensCatalogResetEnabled,
    setKpiSectionSelection,
    shouldBlockDimensionSelection,
    toggleCatalogItemSelection,
} from './scenarioLensCatalog.helpers';
import type { LensCatalogData, LensCatalogItem } from './scenarioLensCatalog.types';
import {
    StyledLensConfigColumn,
    StyledLensConfigColumnHeader,
    StyledLensConfigColumnList,
    StyledLensConfigEmptySearchResult,
    StyledLensConfigItem,
    StyledLensConfigPanelBody,
    StyledLensConfigPanelHeader,
    StyledLensConfigPanelDivider,
    StyledLensConfigContentArea,
    StyledLensConfigEditButton,
    StyledLensConfigColumns,
    StyledLensConfigColumnDivider,
    StyledLensConfigSystemRow,
    StyledLensConfigSystemItem,
    StyledLensConfigFooter,
    StyledLensFooterApplyButton,
    StyledLensFooterResetButton,
} from './ScenarioLensSection.styles';

const LAST_ACTIVE_LENS_TOOLTIP = 'At least one lens must be active';

function getCatalogItemTooltipTitle(
    item: LensCatalogItem,
    isLastSelected: boolean,
    isDimensionLimitBlocked: boolean,
): string | undefined {
    if (isLastSelected) return LAST_ACTIVE_LENS_TOOLTIP;
    if (item.is_disabled) return item.disabled_reason ?? 'Unavailable';
    if (isDimensionLimitBlocked) return LENS_DIMENSION_SELECTION_LIMIT_TOOLTIP;
    return undefined;
}

type LensConfigurationPanelProps = {
    catalog: LensCatalogData;
    baselineCatalog?: LensCatalogData | null;
    onChange: (catalog: LensCatalogData) => void;
    onEdit?: () => void;
    onApply?: () => void;
    onReset?: () => void;
    readOnly?: boolean;
    /** True when this is the only currently active (selected) lens. */
    isLastActiveLens?: boolean;
    /** When set, dimensions and KPIs are filtered to matching display names. */
    searchKeyword?: string;
};

type ConfigColumnProps = {
    title: string;
    items: LensCatalogItem[];
    uiRole: 'DIMENSION' | 'KPI';
    onToggle: (catalogId: number, isSelected: boolean) => void;
    onToggleAll?: (isSelected: boolean) => void;
    showSelectAll?: boolean;
    readOnly?: boolean;
    /**
     * True when this lens is the only active one. The last selected item in each
     * column is protected from being unchecked so at least 1 dimension and 1 KPI
     * always remain active.
     */
    isLastActiveLens?: boolean;
    searchKeyword?: string;
};

type ConfigColumnListProps = {
    displayItems: LensCatalogItem[];
    showSelectAll: boolean;
    onToggleAll?: (isSelected: boolean) => void;
    readOnly: boolean;
    selectableItems: LensCatalogItem[];
    allSelected: boolean;
    someSelected: boolean;
    onToggle: (catalogId: number, isSelected: boolean) => void;
    dimensionLimitState: ReturnType<typeof getDimensionSelectionLimitState> | null;
    isLastActiveLens: boolean;
    selectedCount: number;
    uiRole: 'DIMENSION' | 'KPI';
};

const ConfigColumnList = ({
    displayItems,
    showSelectAll,
    onToggleAll,
    readOnly,
    selectableItems,
    allSelected,
    someSelected,
    onToggle,
    dimensionLimitState,
    isLastActiveLens,
    selectedCount,
    uiRole,
}: ConfigColumnListProps) => (
    <StyledLensConfigColumnList>
        {showSelectAll && onToggleAll && displayItems.length > 0 ? (
            <StyledLensConfigItem isSelected={allSelected} isDisabled={false}>
                <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    disabled={readOnly || selectableItems.length === 0}
                    onChange={(event) => onToggleAll(event.target.checked)}
                    className="lens-config-item-checkbox !m-0"
                    data-testid={ppaSimLensConfigSelectAllTestId(
                        uiRole === 'DIMENSION' ? 'dimension' : 'kpi',
                    )}
                >
                    (All)
                </Checkbox>
            </StyledLensConfigItem>
        ) : null}
        {displayItems.map((item) => {
            const isDimensionLimitBlocked =
                dimensionLimitState != null &&
                shouldBlockDimensionSelection(item, dimensionLimitState.isLimitReached);
            const isLastSelected = isLastActiveLens && item.is_selected && selectedCount === 1;
            const isItemDisabled =
                readOnly || item.is_disabled || isDimensionLimitBlocked || isLastSelected;
            const tooltipTitle = getCatalogItemTooltipTitle(
                item,
                isLastSelected,
                isDimensionLimitBlocked,
            );

            return (
                <Tooltip
                    key={item.catalog_id}
                    {...(tooltipTitle ? CUSTOM_LENS_TOOLTIP_PROPS : {})}
                    title={tooltipTitle}
                >
                    <span className="block w-full">
                        <StyledLensConfigItem isDisabled={isItemDisabled} isSelected={item.is_selected}>
                            <Checkbox
                                checked={item.is_selected}
                                disabled={isItemDisabled}
                                onChange={(event) => onToggle(item.catalog_id, event.target.checked)}
                                className="lens-config-item-checkbox !m-0"
                                data-testid={ppaSimLensConfigOptionTestId(item.attribute_key)}
                            >
                                {item.display_name}
                            </Checkbox>
                        </StyledLensConfigItem>
                    </span>
                </Tooltip>
            );
        })}
    </StyledLensConfigColumnList>
);

const ConfigColumn = ({
    title,
    items,
    uiRole,
    onToggle,
    onToggleAll,
    showSelectAll = false,
    readOnly = false,
    isLastActiveLens = false,
    searchKeyword = '',
}: ConfigColumnProps) => {
    const isDimensionColumn = uiRole === 'DIMENSION';
    const isSearchActive = searchKeyword.trim().length > 0;

    const selectableItems = useMemo(() => items.filter((item) => !item.is_disabled), [items]);

    const displayItems = useMemo(
        () => (isSearchActive ? filterCatalogItems(selectableItems, searchKeyword) : selectableItems),
        [isSearchActive, searchKeyword, selectableItems],
    );

    const dimensionLimitState = useMemo(
        () => (isDimensionColumn ? getDimensionSelectionLimitState(selectableItems) : null),
        [isDimensionColumn, selectableItems],
    );

    const selectedCount = useMemo(
        () => selectableItems.filter((item) => item.is_selected).length,
        [selectableItems],
    );

    const countItems = isSearchActive ? displayItems : selectableItems;

    const { label: selectionLabel } = useMemo(
        () =>
            getCatalogSelectionSummary(
                countItems,
                isDimensionColumn && !isSearchActive ? DEFAULT_LENS_DIMENSION_SELECTION_COUNT : undefined,
            ),
        [countItems, isDimensionColumn, isSearchActive],
    );

    const showDimensionCount = isDimensionColumn && (!isSearchActive || displayItems.length > 0);
    const allSelected = selectableItems.length > 0 && selectedCount === selectableItems.length;
    const someSelected = selectedCount > 0;

    if (!isSearchActive && selectableItems.length === 0) {
        return null;
    }

    return (
        <StyledLensConfigColumn data-testid={ppaSimLensConfigColumnTestId(isDimensionColumn ? 'dimension' : 'kpi')}>
            <StyledLensConfigColumnHeader uiRole={uiRole}>
                {isDimensionColumn ? (
                    <>
                        {title}
                        {showDimensionCount ? (
                            <span className="column-count"> ({selectionLabel})</span>
                        ) : null}
                    </>
                ) : (
                    title
                )}
            </StyledLensConfigColumnHeader>

            {isSearchActive && displayItems.length === 0 ? (
                <StyledLensConfigEmptySearchResult>No results</StyledLensConfigEmptySearchResult>
            ) : (
                <ConfigColumnList
                    displayItems={displayItems}
                    showSelectAll={showSelectAll}
                    onToggleAll={onToggleAll}
                    readOnly={readOnly}
                    selectableItems={selectableItems}
                    allSelected={allSelected}
                    someSelected={someSelected}
                    onToggle={onToggle}
                    dimensionLimitState={dimensionLimitState}
                    isLastActiveLens={isLastActiveLens}
                    selectedCount={selectedCount}
                    uiRole={uiRole}
                />
            )}
        </StyledLensConfigColumn>
    );
};

const LensConfigurationPanel = ({
    catalog,
    baselineCatalog = null,
    onChange,
    onEdit,
    onApply,
    onReset,
    readOnly = false,
    isLastActiveLens = false,
    searchKeyword = '',
}: LensConfigurationPanelProps) => {
    const canApply = useMemo(
        () => isLensCatalogApplyEnabled(catalog, baselineCatalog),
        [catalog, baselineCatalog],
    );
    const canReset = useMemo(
        () => isLensCatalogResetEnabled(catalog, baselineCatalog),
        [catalog, baselineCatalog],
    );

    const systemColumnItems = getSystemColumnItems(catalog);
    const dimensionsSection = getDimensionsSection(catalog);
    const kpisSection = getKpisSection(catalog);
    const dimensionItems = getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.DIMENSIONS);
    const kpiItems = getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.KPIS);
    const dimensionsTitle = dimensionsSection?.section_name ?? 'Dimensions';
    const kpisTitle = kpisSection?.section_name ?? 'User Inputs / KPIs';

    const handleToggle = (catalogId: number, isSelected: boolean) => {
        onChange(toggleCatalogItemSelection(catalog, catalogId, isSelected));
    };

    const handleToggleAllKpis = (isSelected: boolean) => {
        let updatedCatalog = setKpiSectionSelection(catalog, isSelected);

        // Guard: when deselecting all KPIs on the last active lens, keep the first
        // available KPI selected so the lens always has at least 1 KPI.
        if (!isSelected && isLastActiveLens) {
            const firstKpi = getCatalogSectionItems(updatedCatalog, LENS_CATALOG_SECTION_KEYS.KPIS).find(
                (item) => !item.is_disabled,
            );
            if (firstKpi) {
                updatedCatalog = toggleCatalogItemSelection(updatedCatalog, firstKpi.catalog_id, true);
            }
        }

        onChange(updatedCatalog);
    };

    return (
        <StyledLensConfigPanelBody data-testid={PPA_SIM_LENS_CONFIG_BODY}>
            {!readOnly && onEdit ? (
                <>
                    <StyledLensConfigPanelHeader>
                        <StyledLensConfigEditButton
                            type="button"
                            onClick={onEdit}
                            data-testid={PPA_SIM_LENS_CONFIG_EDIT_BTN}
                        >
                            Edit
                        </StyledLensConfigEditButton>
                    </StyledLensConfigPanelHeader>
                    <StyledLensConfigPanelDivider aria-hidden />
                </>
            ) : null}

            <StyledLensConfigContentArea data-testid={PPA_SIM_LENS_CONFIG_CONTENT}>
                {systemColumnItems.length > 0 ? (
                    <StyledLensConfigSystemRow data-testid={PPA_SIM_LENS_CONFIG_SYSTEM_ROW}>
                        {systemColumnItems.map((item) => {
                            const isItemDisabled = readOnly || item.is_disabled;

                            return (
                                <Tooltip
                                    key={item.catalog_id}
                                    title={item.is_disabled ? (item.disabled_reason ?? 'Unavailable') : undefined}
                                >
                                    <StyledLensConfigSystemItem
                                        isDisabled={isItemDisabled}
                                        isSelected={item.is_selected}
                                    >
                                        <Checkbox
                                            checked={item.is_selected}
                                            disabled={isItemDisabled}
                                            onChange={(event) => handleToggle(item.catalog_id, event.target.checked)}
                                            className="lens-config-item-checkbox !m-0"
                                            data-testid={ppaSimLensConfigOptionTestId(item.attribute_key)}
                                        >
                                            {item.display_name}
                                        </Checkbox>
                                    </StyledLensConfigSystemItem>
                                </Tooltip>
                            );
                        })}
                    </StyledLensConfigSystemRow>
                ) : null}

                <StyledLensConfigColumns>
                    {dimensionsSection ? (
                        <ConfigColumn
                            title={dimensionsTitle}
                            items={dimensionItems}
                            uiRole="DIMENSION"
                            onToggle={handleToggle}
                            readOnly={readOnly}
                            isLastActiveLens={isLastActiveLens}
                            searchKeyword={searchKeyword}
                        />
                    ) : null}
                    {dimensionsSection && kpisSection ? (
                        <StyledLensConfigColumnDivider aria-hidden />
                    ) : null}
                    {kpisSection ? (
                        <ConfigColumn
                            title={kpisTitle}
                            items={kpiItems}
                            uiRole="KPI"
                            onToggle={handleToggle}
                            onToggleAll={handleToggleAllKpis}
                            showSelectAll
                            readOnly={readOnly}
                            isLastActiveLens={isLastActiveLens}
                            searchKeyword={searchKeyword}
                        />
                    ) : null}
                </StyledLensConfigColumns>
            </StyledLensConfigContentArea>

            {!readOnly && onApply && onReset ? (
                <>
                    <StyledLensConfigPanelDivider aria-hidden />
                    <StyledLensConfigFooter data-testid={PPA_SIM_LENS_CONFIG_FOOTER}>
                        <StyledLensFooterResetButton
                            disabled={!canReset}
                            onClick={onReset}
                            data-testid={PPA_SIM_LENS_CONFIG_RESET_BTN}
                        >
                            Reset
                        </StyledLensFooterResetButton>
                        <StyledLensFooterApplyButton
                            type="primary"
                            disabled={!canApply}
                            onClick={onApply}
                            data-testid={PPA_SIM_LENS_CONFIG_APPLY_BTN}
                        >
                            Apply
                        </StyledLensFooterApplyButton>
                    </StyledLensConfigFooter>
                </>
            ) : null}
        </StyledLensConfigPanelBody>
    );
};

export default LensConfigurationPanel;
